import React, {useEffect, useState, useMemo} from 'react'
import {Line} from 'react-chartjs-2'
import 'chart.js/auto'
import {FAST_API_URL, API_URL} from '../../../modules/auth/core/_requests'
import { useThemeMode } from '../../../../_designereyes/partials'

// -----------------------
// Data Interfaces
// -----------------------
interface MergedDataItem {
  date: string
  previous_date: string
  current: {
    by_store: {[store: string]: {units: number; revenue: number}}
    total: {units: number; revenue: number}
  }
  previous: {
    by_store: {[store: string]: {units: number; revenue: number}}
    total: {units: number; revenue: number}
  }
}

interface AmazonDataItem {
  date: string
  ecUnits: number
  ecRevenue: number
  vintageUnits: number
  vintageRevenue: number
  allUnits: number
  allRevenue: number
}

// -----------------------
// Store Lists & Colors
// -----------------------
const STORE_OPTIONS = [
  'All Stores', // not used
  'R001 - POV Sawgrass',
  'R002 - Just One Look',
  'R003 - Eyes on Lincoln',
  'R004 - PV2 Dolphin',
  'R005 - DE Mall of San Juan',
  'R006 - DE Brickell',
  // 'R008 - DE Florida Mall',
  'R009 - Aventura',
  // 'R010 - PV3 iDrive',
  // 'R011 - PV4 Vineland',
  'R014 - DE Plaza Las Americas',
]

const availableShopifyStores = STORE_OPTIONS.filter((store) => store !== 'All Stores')
const availableAmazonStores = ['ECAMZ', 'VINTAGE']

// Fixed colors for Amazon stores
const amazonStoreColors: {[key: string]: string} = {
  ECAMZ: '#FF5733', // example color for ECAMZ
  VINTAGE: '#33C3FF', // example color for VINTAGE
}

// Shopify color palette
const shopifyColorPalette = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#66FF66',
  '#FF66CC',
  '#66CCFF',
  '#3399FF',
  '#C9CBCF',
]
const getShopifyStoreColor = (store: string): string => {
  const index = availableShopifyStores.indexOf(store)
  return shopifyColorPalette[index % shopifyColorPalette.length]
}

const combinedShopifyColor = '#00BFFF' // Shopify Combined line color
const combinedAmazonColor = '#FF8C00' // Amazon Combined line color

// -----------------------
// Date Range Helpers
// -----------------------
const DATE_OPTIONS = ['last7Days', 'last30Days', 'last60Days', 'last90Days', 'YTD', 'lastYear']

const getDateRange = (option: string): {min: string; max: string} => {
  const today = new Date()
  let minDate: Date
  let maxDate: Date = today
  switch (option) {
    case 'last7Days':
      minDate = new Date(today)
      minDate.setDate(minDate.getDate() - 6)
      break
    case 'last30Days':
      minDate = new Date(today)
      minDate.setDate(minDate.getDate() - 29)
      break
    case 'last60Days':
      minDate = new Date(today)
      minDate.setDate(minDate.getDate() - 59)
      break
    case 'last90Days':
      minDate = new Date(today)
      minDate.setDate(minDate.getDate() - 89)
      break
    case 'YTD':
      minDate = new Date(today.getFullYear(), 0, 1)
      break
    case 'lastYear':
      minDate = new Date(today.getFullYear() - 1, 0, 1)
      maxDate = new Date(today.getFullYear() - 1, 11, 31)
      break
    default:
      minDate = new Date(today)
      minDate.setDate(minDate.getDate() - 29)
  }
  const formatDate = (d: Date) => d.toISOString().split('T')[0]
  return {min: formatDate(minDate), max: formatDate(maxDate)}
}

/**
 * getAmazonDateRanges computes boundaries for:
 * - current: the selected period (e.g. last 30 days, YTD, etc.)
 * - previous: the immediately preceding period of the same length.
 */
const getAmazonDateRanges = (option: string) => {
  const today = new Date()
  const currentEnd = new Date(today)
  currentEnd.setDate(currentEnd.getDate() - 1)
  let periodLength = 30
  if (option === 'last7Days') periodLength = 7
  else if (option === 'last30Days') periodLength = 30
  else if (option === 'last60Days') periodLength = 60
  else if (option === 'last90Days') periodLength = 90
  else if (option === 'YTD') {
    const jan1 = new Date(today.getFullYear(), 0, 1)
    periodLength = Math.floor((currentEnd.getTime() - jan1.getTime()) / (1000 * 60 * 60 * 24)) + 1
  } else if (option === 'lastYear') {
    periodLength = 365
  }
  const currentStart = new Date(currentEnd)
  currentStart.setDate(currentStart.getDate() - periodLength + 1)
  const previousEnd = new Date(currentStart)
  previousEnd.setDate(previousEnd.getDate() - 1)
  const previousStart = new Date(previousEnd)
  previousStart.setDate(previousStart.getDate() - periodLength + 1)
  return {
    current: {
      min: currentStart.toISOString().split('T')[0],
      max: currentEnd.toISOString().split('T')[0],
    },
    previous: {
      min: previousStart.toISOString().split('T')[0],
      max: previousEnd.toISOString().split('T')[0],
    },
  }
}

// -----------------------
// Styling Helpers
// -----------------------
const getChangeStyle = (change: number) => {
  if (change > 0) {
    return {
      backgroundColor: '#d3f8d3',
      color: 'green',
      border: '1px solid green',
      borderRadius: '999px',
      padding: '2px 6px',
      fontSize: '0.75rem',
      marginLeft: '4px',
    }
  } else if (change < 0) {
    return {
      backgroundColor: '#fac5c5',
      color: 'red',
      border: '1px solid red',
      borderRadius: '999px',
      padding: '2px 6px',
      fontSize: '0.75rem',
      marginLeft: '4px',
    }
  } else {
    return {
      backgroundColor: 'rgba(128,128,128,0.1)',
      color: 'gray',
      border: '1px solid gray',
      borderRadius: '999px',
      padding: '2px 6px',
      fontSize: '0.75rem',
      marginLeft: '4px',
    }
  }
}

// -----------------------
// Main Component
// -----------------------
const ShopifySalesDashboard: React.FC = () => {
  const {mode} = useThemeMode();
  const axisColor = mode == 'light' ? '#000' : 'rgba(255,255,255,0.2)';
  const gridColor = mode == 'light' ? '#000' : '#fff';
  // Shopify state
  const [mergedData, setMergedData] = useState<MergedDataItem[]>([])
  const [loadingShopify, setLoadingShopify] = useState<boolean>(true)
  const [dateRangeOption, setDateRangeOption] = useState('last30Days')
  const [activeTab, setActiveTab] = useState<'units' | 'revenue'>('units')
  const [selectedShopifyStores, setSelectedShopifyStores] = useState<string[]>([
    ...availableShopifyStores,
  ])
  const [shopifyCombined, setShopifyCombined] = useState<boolean>(false)

  // Amazon state (single state holding all daily data)
  const [amazonData, setAmazonData] = useState<AmazonDataItem[]>([])
  const [loadingAmazon, setLoadingAmazon] = useState<boolean>(true)
  const [selectedAmazonStores, setSelectedAmazonStores] = useState<string[]>([
    ...availableAmazonStores,
  ])
  const [amazonCombined, setAmazonCombined] = useState<boolean>(false)

  // Sidebar collapse state
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false)

  // Fetch Shopify data
  useEffect(() => {
    async function fetchShopifyData() {
      setLoadingShopify(true)
      const {min, max} = getDateRange(dateRangeOption)
      const url = `${FAST_API_URL}/shopify_sales?min_date=${min}&max_date=${max}`
      try {
        const resp = await fetch(url)
        const json = await resp.json()
        setMergedData(json.data || [])
      } catch (error) {
        console.error('Error fetching Shopify data:', error)
        setMergedData([])
      } finally {
        setLoadingShopify(false)
      }
    }
    fetchShopifyData()
  }, [dateRangeOption])

  // Fetch Amazon data (all daily records)
  useEffect(() => {
    async function fetchAmazonData() {
      setLoadingAmazon(true)
      try {
        const resp = await fetch(
          `${API_URL}/amazon-line-graph/chart-data?store=both&interval=daily`
        )
        const json = await resp.json()
        setAmazonData(json.data || [])
      } catch (error) {
        console.error('Error fetching Amazon data:', error)
        setAmazonData([])
      } finally {
        setLoadingAmazon(false)
      }
    }
    fetchAmazonData()
  }, [])

  // Compute Amazon current and previous ranges
  const amazonRanges = useMemo(() => getAmazonDateRanges(dateRangeOption), [dateRangeOption])

  // Filter amazon data for current and previous periods (normalize dates)
  const currentAmazonDataFiltered = useMemo(() => {
    return amazonData.filter(
      (item) =>
        item.date.split('T')[0] >= amazonRanges.current.min &&
        item.date.split('T')[0] <= amazonRanges.current.max
    )
  }, [amazonData, amazonRanges.current])
  const previousAmazonDataFiltered = useMemo(() => {
    return amazonData.filter(
      (item) =>
        item.date.split('T')[0] >= amazonRanges.previous.min &&
        item.date.split('T')[0] <= amazonRanges.previous.max
    )
  }, [amazonData, amazonRanges.previous])

  // Overall totals for Shopify (for selected stores)
  const overallShopifyTotals = useMemo(() => {
    if (!mergedData.length) return {current: 0, previous: 0, change: 0}
    const dataKey = activeTab === 'units' ? 'units' : 'revenue'
    let currentSum = 0
    let previousSum = 0
    mergedData.forEach((item) => {
      selectedShopifyStores.forEach((store) => {
        currentSum += item.current?.by_store?.[store]?.[dataKey] ?? 0
        previousSum += item.previous?.by_store?.[store]?.[dataKey] ?? 0
      })
    })
    const change = previousSum === 0 ? 0 : ((currentSum - previousSum) / previousSum) * 100
    return {current: currentSum, previous: previousSum, change}
  }, [mergedData, activeTab, selectedShopifyStores])

  // Overall totals for Amazon (for selected stores) computed from filtered data
  const overallAmazonTotals = useMemo(() => {
    let currentSum = 0
    let previousSum = 0
    selectedAmazonStores.forEach((store) => {
      const field =
        activeTab === 'units'
          ? store === 'ECAMZ'
            ? 'ecUnits'
            : 'vintageUnits'
          : store === 'ECAMZ'
          ? 'ecRevenue'
          : 'vintageRevenue'
      const curTotal = currentAmazonDataFiltered.reduce(
        (sum, item) => sum + Number(item[field] || 0),
        0
      )
      const prevTotal = previousAmazonDataFiltered.reduce(
        (sum, item) => sum + Number(item[field] || 0),
        0
      )
      currentSum += curTotal
      previousSum += prevTotal
    })
    const change = previousSum === 0 ? 0 : ((currentSum - previousSum) / previousSum) * 100
    return {current: currentSum, previous: previousSum, change}
  }, [currentAmazonDataFiltered, previousAmazonDataFiltered, selectedAmazonStores, activeTab])

  // Top overall totals (Shopify + Amazon)
  const overallTopTotals = useMemo(() => {
    const current = overallShopifyTotals.current + overallAmazonTotals.current
    const previous = overallShopifyTotals.previous + overallAmazonTotals.previous
    const change = previous === 0 ? 0 : ((current - previous) / previous) * 100
    return {current, previous, change}
  }, [overallShopifyTotals, overallAmazonTotals])

  // Normalize Shopify dates for chart labels
  const chartLabels = useMemo(() => mergedData.map((item) => item.date.split('T')[0]), [mergedData])

  // Build chart data.
  const chartData = useMemo(() => {
    const dataKeyShopify = activeTab === 'units' ? 'units' : 'revenue'
    const datasets: any[] = []
    // Shopify individual datasets
    selectedShopifyStores.forEach((store) => {
      const storeColor = getShopifyStoreColor(store)
      datasets.push({
        label: store,
        data: chartLabels.map(
          (date) =>
            mergedData.find((item) => item.date.split('T')[0] === date)?.current?.by_store?.[
              store
            ]?.[dataKeyShopify] ?? 0
        ),
        borderColor: storeColor,
        backgroundColor: storeColor + '33',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
      })
    })
    // Shopify combined dataset with assigned color
    if (shopifyCombined) {
      datasets.push({
        label: 'All Shopify Combined',
        data: chartLabels.map((date) =>
          availableShopifyStores.reduce((sum, store) => {
            const record = mergedData.find((item) => item.date.split('T')[0] === date)
            return sum + (record?.current?.by_store?.[store]?.[dataKeyShopify] ?? 0)
          }, 0)
        ),
        borderColor: combinedShopifyColor,
        backgroundColor: combinedShopifyColor + '33',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
      })
    }
    // Amazon individual datasets (for current period only)
    const getAmazonField = (store: string) => {
      if (store === 'ECAMZ') return activeTab === 'units' ? 'ecUnits' : 'ecRevenue'
      if (store === 'VINTAGE') return activeTab === 'units' ? 'vintageUnits' : 'vintageRevenue'
      return activeTab === 'units' ? 'allUnits' : 'allRevenue'
    }
    selectedAmazonStores.forEach((store) => {
      const field = getAmazonField(store)
      datasets.push({
        label: store,
        data: chartLabels.map((date) => {
          if (date >= amazonRanges.current.min && date <= amazonRanges.current.max) {
            const rec = amazonData.find((item) => item.date.split('T')[0] === date)
            return rec ? Number(rec[field] || 0) : 0
          }
          return 0
        }),
        borderColor: amazonStoreColors[store] || '#AAAAAA',
        backgroundColor: (amazonStoreColors[store] || '#AAAAAA') + '33',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
      })
    })
    // Amazon combined dataset with assigned color
    if (amazonCombined) {
      datasets.push({
        label: 'All Amazon Combined',
        data: chartLabels.map((date) => {
          if (date >= amazonRanges.current.min && date <= amazonRanges.current.max) {
            const rec = amazonData.find((item) => item.date.split('T')[0] === date)
            return rec ? Number(activeTab === 'units' ? rec.allUnits || 0 : rec.allRevenue || 0) : 0
          }
          return 0
        }),
        borderColor: combinedAmazonColor,
        backgroundColor: combinedAmazonColor + '33',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
      })
    }
    return {labels: chartLabels, datasets}
  }, [
    mergedData,
    amazonData,
    amazonRanges,
    activeTab,
    chartLabels,
    selectedShopifyStores,
    shopifyCombined,
    selectedAmazonStores,
    amazonCombined,
  ])

  // Sidebar: Totals for a Shopify store.
  const getShopifyStoreTotals = (store: string) => {
    if (!mergedData.length) return {current: 0, previous: 0, change: 0}
    const dataKey = activeTab === 'units' ? 'units' : 'revenue'
    let currentSum = 0
    let previousSum = 0
    mergedData.forEach((item) => {
      currentSum += item.current?.by_store?.[store]?.[dataKey] ?? 0
      previousSum += item.previous?.by_store?.[store]?.[dataKey] ?? 0
    })
    const change = previousSum === 0 ? 0 : ((currentSum - previousSum) / previousSum) * 100
    return {current: currentSum, previous: previousSum, change}
  }

  // Sidebar: Totals for an Amazon store (using filtered data)
  const getAmazonStoreTotalsForSidebar = (store: string) => {
    if (!amazonData.length) return {current: 0, previous: 0, change: 0}
    let currentSum = 0
    let previousSum = 0
    const field =
      activeTab === 'units'
        ? store === 'ECAMZ'
          ? 'ecUnits'
          : 'vintageUnits'
        : store === 'ECAMZ'
        ? 'ecRevenue'
        : 'vintageRevenue'
    // Sum current values from filtered current data
    currentAmazonDataFiltered.forEach((item) => {
      currentSum += Number(item[field] || 0)
    })
    // Sum previous values from filtered previous data
    previousAmazonDataFiltered.forEach((item) => {
      previousSum += Number(item[field] || 0)
    })
    const change = previousSum === 0 ? 0 : ((currentSum - previousSum) / previousSum) * 100
    return {current: currentSum, previous: previousSum, change}
  }

  // Derived state for select-all checkboxes.
  const shopifyAllSelected = selectedShopifyStores.length === availableShopifyStores.length
  const amazonAllSelected = selectedAmazonStores.length === availableAmazonStores.length

  // Toggle functions.
  const toggleSelectAllShopify = () => {
    setSelectedShopifyStores(shopifyAllSelected ? [] : [...availableShopifyStores])
  }
  const toggleShopifyStore = (store: string) => {
    setSelectedShopifyStores((prev) =>
      prev.includes(store) ? prev.filter((s) => s !== store) : [...prev, store]
    )
  }
  const toggleSelectAllAmazon = () => {
    setSelectedAmazonStores(amazonAllSelected ? [] : [...availableAmazonStores])
  }
  const toggleAmazonStore = (store: string) => {
    setSelectedAmazonStores((prev) =>
      prev.includes(store) ? prev.filter((s) => s !== store) : [...prev, store]
    )
  }

  // Toggle sidebar collapse.
  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => !prev)
  }

  // Chart options.
  const chartOptions = useMemo(
    () => ({
      plugins: {
        legend: {display: false},
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const label = ctx.dataset.label || ''
              const dateStr = ctx.chart.data.labels[ctx.dataIndex]
              const value = ctx.parsed.y
              return activeTab === 'revenue'
                ? `${label} (${dateStr}): $${value.toLocaleString()}`
                : `${label} (${dateStr}): ${value.toLocaleString()}`
            },
          },
        },
      },
      scales: {
        x: {ticks: {color: gridColor}, grid: {color: axisColor}},
        y: {ticks: {color: gridColor}, grid: {color: axisColor}},
      },
      responsive: true,
      maintainAspectRatio: false,
    }),
    [activeTab]
  )

  return (
    <div className='card shadow mb-4' style={{height: '100%', padding: '0.7rem'}}>
      <div className='card-header pt-5'>
        <h3>All Sales Chart</h3>
      </div>
      <div className='card-body'>
        {/* Top Row (compressed) */}
        <div className='pt-2 pb-2' style={{display: 'flex', alignItems: 'center', marginBottom: '0.5rem'}}>
          <div className='d-flex align-items-center' style={{flex: 1}}>
            <label htmlFor='dateRangeSelect' style={{fontSize: '0.8rem', marginRight: '0.5rem'}}>
              Date Range:
            </label>
            <select
              className='form-select form-select-sm inline-flex'
              id='dateRangeSelect'
              value={dateRangeOption}
              onChange={(e) => setDateRangeOption(e.target.value)}
              style={{width: 'auto'}}
            >
              {DATE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {loadingAmazon && (
              <span style={{fontSize: '0.8rem', marginLeft: '1rem'}}>Amazon data loading...</span>
            )}
          </div>
          <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
            <button
              className={`btn button ${activeTab === 'units' ? 'btn-primary' : ''}`}
              type='button'
              onClick={() => setActiveTab('units')}
              style={{
                fontSize: '0.8rem',
                padding: '0.2rem 0.4rem',
                border: '1px solid #555',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Units
            </button>
            <button
              type='button'
              className={`btn button ${activeTab === 'revenue' ? 'btn-primary' : ''}`}
              onClick={() => setActiveTab('revenue')}
              style={{
                fontSize: '0.8rem',
                padding: '0.2rem 0.4rem',
                border: '1px solid #555',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Revenue
            </button>
            <div style={{fontSize: '0.8rem', marginLeft: '1rem', whiteSpace: 'nowrap'}}>
              {activeTab === 'units'
                ? `Units: ${overallTopTotals.current.toLocaleString()}`
                : `Revenue: $${overallTopTotals.current.toLocaleString()}`}
              <span style={getChangeStyle(overallTopTotals.change)}>
                {overallTopTotals.change >= 0 ? '▲' : '▼'}{' '}
                {Math.abs(overallTopTotals.change).toFixed(2)}%
              </span>
            </div>
          </div>
        </div>

        {/* Main Content: Sidebar & Chart */}
        <div style={{display: 'flex', width: '100%'}}>
          {/* Sidebar */}
          <div
            style={{
              width: sidebarCollapsed ? '30px' : '250px',
              transition: 'width 0.3s',
              border: '1px solid rgba(255,255,255,0.2)',
              padding: sidebarCollapsed ? '0.25rem' : '0.5rem 1rem',
              boxSizing: 'border-box',
              position: 'relative',
            }}
          >
            {/* Sidebar Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem',
              }}
            >
              {!sidebarCollapsed && <span style={{fontSize: '0.9rem'}}>Stores</span>}
              <button
                onClick={toggleSidebar}
                style={{
                  border: 'none',
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
                title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
              >
                {sidebarCollapsed ? '►' : '◀'}
              </button>
            </div>
            {!sidebarCollapsed && (
              <div style={{overflowY: 'auto', height: 'calc(400px - 2rem)', paddingRight: '1rem'}}>
                {/* Shopify Section */}
                <div style={{marginBottom: '0.5rem'}}>
                  <div
                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                  >
                    <h6 style={{fontSize: '0.85rem', margin: 0}}>Shopify Stores</h6>
                    <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                      <label htmlFor='shopifyCombined' style={{fontSize: '0.75rem'}}>
                        Combined
                      </label>
                      <input
                        type='checkbox'
                        className='form-checkbox form-input'
                        id='shopifyCombined'
                        checked={shopifyCombined}
                        onChange={() => setShopifyCombined((prev) => !prev)}
                      />
                      <label htmlFor='shopifySelectAll' style={{fontSize: '0.75rem'}}>
                        All
                      </label>
                      <input
                        type='checkbox'
                        className='form-checkbox form-input'
                        id='shopifySelectAll'
                        checked={shopifyAllSelected}
                        onChange={toggleSelectAllShopify}
                      />
                    </div>
                  </div>
                  {availableShopifyStores.map((store) => {
                    const totals = getShopifyStoreTotals(store)
                    return (
                      <div key={store} style={{marginTop: '0.25rem'}}>
                        <div style={{display: 'flex', alignItems: 'center'}}>
                          <input
                            type='checkbox'
                            className='form-checkbox form-input'
                            id={store}
                            checked={selectedShopifyStores.includes(store)}
                            onChange={() => toggleShopifyStore(store)}
                            style={{marginRight: '0.5rem'}}
                          />
                          <label
                            htmlFor={store}
                            style={{
                              color: getShopifyStoreColor(store),
                              fontSize: '0.85rem',
                              margin: 0,
                            }}
                          >
                            {store}
                          </label>
                        </div>
                        <div style={{marginLeft: '1.5rem'}}>
                          <small style={{fontSize: '0.75rem'}}>
                            {activeTab === 'units'
                              ? `Units: ${totals.current.toLocaleString()}`
                              : `Revenue: $${totals.current.toLocaleString()}`}
                            <span style={getChangeStyle(totals.change)}>
                              {totals.change >= 0 ? '▲' : '▼'} {Math.abs(totals.change).toFixed(2)}%
                            </span>
                          </small>
                        </div>
                      </div>
                    )
                  })}
                </div>
                <hr style={{borderColor: 'rgba(255,255,255,0.2)'}} />
                {/* Amazon Section */}
                <div>
                  <div
                    style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                  >
                    <h6 style={{fontSize: '0.85rem', margin: 0}}>Amazon Stores</h6>
                    <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                      <label htmlFor='amazonCombined' style={{fontSize: '0.75rem'}}>
                        Combined
                      </label>
                      <input
                        type='checkbox'
                        id='amazonCombined'
                        className='form-checkbox form-input'
                        checked={amazonCombined}
                        onChange={() => setAmazonCombined((prev) => !prev)}
                      />
                      <label htmlFor='amazonSelectAll' style={{fontSize: '0.75rem'}}>
                        All
                      </label>
                      <input
                        type='checkbox'
                        id='amazonSelectAll'
                        className='form-checkbox form-input'
                        checked={amazonAllSelected}
                        onChange={toggleSelectAllAmazon}
                      />
                    </div>
                  </div>
                  {availableAmazonStores.map((store) => {
                    const totals = getAmazonStoreTotalsForSidebar(store)
                    return (
                      <div key={store} style={{marginTop: '0.25rem'}}>
                        <div style={{display: 'flex', alignItems: 'center'}}>
                          <input
                            type='checkbox'
                            id={store}
                            className='form-checkbox form-input'
                            checked={selectedAmazonStores.includes(store)}
                            onChange={() => toggleAmazonStore(store)}
                            style={{marginRight: '0.5rem'}}
                          />
                          <label
                            htmlFor={store}
                            style={{
                              fontSize: '0.85rem',
                              color: amazonStoreColors[store] || '#AAAAAA',
                            }}
                          >
                            {store}
                          </label>
                        </div>
                        <div style={{marginLeft: '1.5rem'}}>
                          <small style={{fontSize: '0.75rem'}}>
                            {activeTab === 'units'
                              ? `Units: ${totals.current.toLocaleString()}`
                              : `Revenue: $${totals.current.toLocaleString()}`}
                            <span style={getChangeStyle(totals.change)}>
                              {totals.change >= 0 ? '▲' : '▼'} {Math.abs(totals.change).toFixed(2)}%
                            </span>
                          </small>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
          {/* Chart Container */}
          <div style={{flex: 1, minWidth: 0}}>
            <div className='' style={{height: '400px'}}>
              {loadingShopify ? (
                <div className='d-flex justify-content-center align-items-center h-100'>
                  <span>Loading chart...</span>
                </div>
              ) : (
                <Line data={chartData} options={chartOptions} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShopifySalesDashboard
